import React, { useCallback, useEffect, useRef, useState, useContext } from 'react'
import { VariableSizeList, ListChildComponentProps } from 'react-window'
import { ListSubheader, Radio, IconButton } from '@mui/material'
import { Check, ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material'
import { ISelectorOption } from '@trivie/core'
import { color } from '@trivie/ui-web/theme/color'
import { Text } from '@trivie/ui-web/components/text'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { getTypeIcon } from '../autocomplete-selector'
import { SelectorListboxContext, SelectorListboxContextValue } from './listbox-context'
import { Image } from '@trivie/ui-web/components/image'
import defaultCover from '@trivie/ui-web/components/data-table/default-topic-image.png'

const LISTBOX_PADDING = 8
const ITEM_SIZE = 48
const SUB_ITEM_SIZE = 48

type GroupHeader = { key: string; group: string; itemCount: number }
type CollapsedGroupHeader = { key: string; collapsedGroup: string; itemCount: number }
type OptionTuple = [any, ISelectorOption, number]
type SubItemRow = { parentOption: ISelectorOption; subItem: ISelectorOption }
type FlatItem = GroupHeader | CollapsedGroupHeader | OptionTuple | SubItemRow

function isGroupHeader(item: FlatItem): item is GroupHeader {
  return !Array.isArray(item) && 'group' in item
}

function isCollapsedGroupHeader(item: FlatItem): item is CollapsedGroupHeader {
  return !Array.isArray(item) && 'collapsedGroup' in item
}

function isSubItemRow(item: FlatItem): item is SubItemRow {
  return !Array.isArray(item) && 'subItem' in item
}

function RenderRow(props: ListChildComponentProps) {
  const { data, index, style: rowStyle } = props
  const { items, ctx } = data as { items: FlatItem[]; ctx: SelectorListboxContextValue }
  const dataSet = items[index]
  const inlineStyle = {
    ...rowStyle,
    top: ((rowStyle.top as number) ?? 0) + LISTBOX_PADDING,
  }

  // Collapsed group header — single clickable row to expand
  if (isCollapsedGroupHeader(dataSet)) {
    return (
      <ListSubheader
        key={dataSet.key}
        component="div"
        onClick={() => ctx.onToggleGroupCollapse(dataSet.collapsedGroup)}
        style={{
          ...inlineStyle,
          fontWeight: 600,
          textTransform: 'uppercase',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          height: ITEM_SIZE,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <span>{dataSet.collapsedGroup}</span>
        <IconButton size="small" style={{ padding: 2 }} tabIndex={-1}>
          <ExpandMoreRounded />
        </IconButton>
      </ListSubheader>
    )
  }

  // Group header row
  if (isGroupHeader(dataSet)) {
    const collapsible = ctx.collapsibleGroups
    return (
      <ListSubheader
        key={dataSet.key}
        component="div"
        onClick={collapsible ? () => ctx.onToggleGroupCollapse(dataSet.group) : undefined}
        style={{
          ...inlineStyle,
          fontWeight: 600,
          textTransform: 'uppercase',
          backgroundColor: '#fff',
          ...(collapsible
            ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                height: ITEM_SIZE,
              }
            : {}),
        }}
      >
        {collapsible ? (
          <>
            <span>{dataSet.group}</span>
            <IconButton size="small" style={{ padding: 2 }} tabIndex={-1}>
              <ExpandLessRounded />
            </IconButton>
          </>
        ) : (
          dataSet.group
        )}
      </ListSubheader>
    )
  }

  // SubItem row (child of an expanded parent)
  if (isSubItemRow(dataSet)) {
    const { subItem } = dataSet
    return (
      <li
        key={`sub_${subItem.id}_${subItem.type}`}
        style={{
          ...inlineStyle,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 56,
          boxSizing: 'border-box',
          listStyle: 'none',
        }}
      >
        {getTypeIcon(subItem.type, 18) && (
          <div style={SUB_ITEM_ICON}>{getTypeIcon(subItem.type, 18)}</div>
        )}
        <div style={{ marginLeft: 12, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Text variant="body1Medium" text={subItem.label} />
          {!!subItem.subLabel && (
            <Text variant="body2Medium" text={subItem.subLabel} style={{ color: color.shade30 }} />
          )}
        </div>
      </li>
    )
  }

  // Option row (from tuple [props, option, index])
  const { key, ...optionProps } = dataSet[0]
  const option: ISelectorOption = dataSet[1]
  const sel = ctx.selectedOptionsRef.current.some(
    (o: ISelectorOption) => o.type === option.type && o.id === option.id,
  )
  const isExpanded = ctx.expandedIds.has(option.id)
  const hasSubItems = option.subItems && option.subItems.length > 0

  return (
    <li
      key={key}
      {...optionProps}
      style={{
        ...inlineStyle,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 8,
        boxSizing: 'border-box',
      }}
    >
      <Radio
        checked={sel}
        checkedIcon={
          <div style={CHECK_ICON}>
            <Check htmlColor={color.white} style={{ fontSize: 16 }} />
          </div>
        }
        color="default"
        inputProps={{ 'aria-label': 'choice' }}
        style={{
          marginRight: 8,
          placeSelf: 'center',
          color: sel ? color.blue : color.shade30,
        }}
      />
      {ctx.showAvatar && option.type === 'Users' ? (
        <Avatar
          style={{ marginRight: 8, position: 'relative' }}
          fontStyle={{ fontSize: 14 }}
          size={32}
          name={option.subLabel ? option.label : ''}
          url={option.photo_url}
        />
      ) : null}
      {option.type === 'topics' ? (
        <Image
          src={option.photo_url || defaultCover}
          style={{ height: 30, width: 60, marginRight: 16 }}
        />
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Text variant="body1Medium" text={option.label} />
        {!!option.subLabel && (
          <Text variant="body2Medium" text={option.subLabel} style={{ color: color.shade30 }} />
        )}
      </div>
      {hasSubItems && (
        <IconButton
          style={{ marginLeft: 'auto' }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            ctx.onToggleExpand(option.id)
          }}
          size="small"
        >
          {isExpanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
        </IconButton>
      )}
    </li>
  )
}

function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null)
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, false)
    }
  }, [data])
  return ref
}

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

const InnerElement = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  (props, ref) => (
    <ul ref={ref} {...props} style={{ ...props.style, paddingTop: LISTBOX_PADDING }} />
  ),
)

export const VirtualizedListbox = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function VirtualizedListbox(listProps, ref) {
  const ctx = useContext(SelectorListboxContext)
  // Separate style from other props — MUI passes padding in style that conflicts with our LISTBOX_PADDING
  const { children, className, style: _muiStyle, ...other } = listProps as any

  const [stickyGroup, setStickyGroup] = useState<string | null>(null)
  const [containerHeight, setContainerHeight] = useState<number>(0)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const scrollOffsetRef = useRef<number>(0)

  // Measure available height from the flex container
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      wrapperRef.current = node
      // Forward the ref from MUI
      if (typeof ref === 'function') ref(node)
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerHeight(entry.contentRect.height)
        }
      })
      observer.observe(node)
      return () => observer.disconnect()
    }
  }, [])

  // Merge children by group name (MUI may split same-name groups if options aren't contiguous)
  const mergedGroups = new Map<string, any[]>()
  const groupOrder: string[] = []

  ;(children ?? []).forEach((child: any) => {
    if (child && typeof child === 'object' && 'group' in child) {
      const groupName = child.group as string
      const groupChildren = Array.isArray(child.children) ? child.children : []
      if (!mergedGroups.has(groupName)) {
        mergedGroups.set(groupName, [])
        groupOrder.push(groupName)
      }
      mergedGroups.get(groupName)!.push(...groupChildren)
    }
  })

  // Flatten merged groups into a single itemData array
  const itemData: FlatItem[] = []
  groupOrder.forEach((groupName, idx) => {
    const groupChildren = mergedGroups.get(groupName)!
    if (groupChildren.length > 0) {
      // Collapsed groups render as a single compact row in-place
      if (ctx.collapsibleGroups && ctx.collapsedGroups.has(groupName)) {
        itemData.push({
          key: `collapsed-${idx}`,
          collapsedGroup: groupName,
          itemCount: groupChildren.length,
        })
        return
      }
      itemData.push({ key: `group-${idx}`, group: groupName, itemCount: groupChildren.length })
      groupChildren.forEach((tuple: any) => {
        itemData.push(tuple)
        // If this option is expanded and has subItems, inject them as separate rows
        if (Array.isArray(tuple) && tuple[1]) {
          const option = tuple[1] as ISelectorOption
          if (ctx.expandedIds.has(option.id) && option.subItems && option.subItems.length > 0) {
            option.subItems.forEach((subItem) => {
              itemData.push({ parentOption: option, subItem })
            })
          }
        }
      })
    }
  })

  const itemCount = itemData.length

  const GROUP_HEADER_SIZE = 40 // Minimal height — sticky overlay handles the visual display

  const getChildSize = (index: number) => {
    const child = itemData[index]
    if (isCollapsedGroupHeader(child)) return ITEM_SIZE
    if (isSubItemRow(child)) return SUB_ITEM_SIZE
    if (isGroupHeader(child)) return ctx.collapsibleGroups ? ITEM_SIZE : GROUP_HEADER_SIZE
    return ITEM_SIZE
  }

  const getContentHeight = () => {
    return itemData.reduce((sum, _, i) => sum + getChildSize(i), 0) + 2 * LISTBOX_PADDING
  }

  const getHeight = () => {
    const contentHeight = getContentHeight()
    // If container has been measured via flex, use the smaller of content or container
    if (containerHeight > 0) {
      return Math.min(contentHeight, containerHeight)
    }
    // Fallback: cap at 8 items
    if (itemCount > 8) {
      return 8 * ITEM_SIZE + LISTBOX_PADDING
    }
    return contentHeight
  }

  // Reset cache when itemCount changes (e.g., expand/collapse) or expandedIds/collapsedGroups change
  const gridRef = useResetCache(
    `${itemCount}-${Array.from(ctx.expandedIds).join(',')}-${Array.from(ctx.collapsedGroups).join(',')}`,
  )

  const computeStickyGroup = (scrollOffset: number) => {
    let currentGroup: string | null = null
    let cumulativeHeight = 0
    for (let i = 0; i < itemData.length; i++) {
      if (cumulativeHeight > scrollOffset + LISTBOX_PADDING) break
      const item = itemData[i]
      if (isGroupHeader(item)) {
        currentGroup = item.group
      }
      // Collapsed headers have no scrollable section — clear the sticky
      if (isCollapsedGroupHeader(item)) {
        currentGroup = null
      }
      cumulativeHeight += getChildSize(i)
    }
    return currentGroup
  }

  // Recompute sticky group when collapsed groups change, using actual scroll position
  useEffect(() => {
    setStickyGroup(computeStickyGroup(scrollOffsetRef.current))
  }, [Array.from(ctx.collapsedGroups).join(',')])

  const handleScroll = ({ scrollOffset }: { scrollOffset: number }) => {
    scrollOffsetRef.current = scrollOffset
    setStickyGroup(computeStickyGroup(scrollOffset))
  }

  const listData = { items: itemData, ctx }

  return (
    <div
      ref={measureRef}
      {...other}
      style={{
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Virtual list area with sticky overlay for current expanded group */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {stickyGroup && (
          <ListSubheader
            component="div"
            style={{
              position: 'absolute',
              top: 0,
              paddingTop: 8,
              left: 0,
              right: 0,
              zIndex: 2,
              fontWeight: 600,
              textTransform: 'uppercase',
              backgroundColor: '#fff',
              ...(ctx.collapsibleGroups
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }
                : {}),
            }}
            onClick={
              ctx.collapsibleGroups ? () => ctx.onToggleGroupCollapse(stickyGroup) : undefined
            }
          >
            {ctx.collapsibleGroups ? (
              <>
                <span>{stickyGroup}</span>
                <IconButton size="small" style={{ padding: 2 }} tabIndex={-1}>
                  <ExpandLessRounded />
                </IconButton>
              </>
            ) : (
              stickyGroup
            )}
          </ListSubheader>
        )}
        <OuterElementContext.Provider value={{}}>
          <VariableSizeList
            className={className}
            itemData={listData}
            height={getHeight()}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType={InnerElement}
            itemSize={getChildSize}
            overscanCount={5}
            itemCount={itemCount}
            onScroll={handleScroll}
          >
            {RenderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    </div>
  )
})

const CHECK_ICON: React.CSSProperties = {
  backgroundColor: color.blue,
  height: 20,
  width: 20,
  marginLeft: 2,
  position: 'relative',
  borderRadius: 100,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginRight: 2,
}

const SUB_ITEM_ICON: React.CSSProperties = {
  height: 32,
  width: 32,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  borderRadius: 6,
  alignItems: 'center',
}
